import React from 'react';

/**
 * SchoolOne logo — the open-book "1" mark supplied Sept 2026, served from
 * /public/brand. The `variant` prop is kept for API compatibility with the
 * older generated icons; every variant now renders the same mark.
 * `size` controls the icon height in px; the wordmark scales accordingly.
 * `theme="light"` = on a dark background (sidebar): the mark sits on a white
 *   tile, because its navy has no contrast against the navy sidebar.
 * `theme="dark"`  = on a white background (login page, etc.).
 */
export type LogoVariant = 'bars' | 'tick' | 'crest';

interface LogoProps {
  variant?: LogoVariant;
  /** Icon height in px — wordmark scales proportionally */
  size?: number;
  /** Controls icon colour palette */
  theme?: 'light' | 'dark';
  /** Show "SchoolOne" wordmark alongside the icon */
  showWordmark?: boolean;
  className?: string;
}

/** Brand colours taken from the supplied artwork. */
export const BRAND_NAVY = '#00265B';
export const BRAND_SKY = '#01B7FE';
export const BRAND_TAGLINE = 'Smarter tools for better-run schools.';

export const BRAND_MARK_URL = '/brand/schoolone-mark.png';
export const BRAND_BANNER_URL = '/brand/schoolone-banner.png';

function Mark({ size, theme }: { size: number; theme: 'light' | 'dark' }) {
  const img = (
    <img
      src={BRAND_MARK_URL}
      alt=""
      aria-hidden="true"
      width={size}
      height={size}
      draggable={false}
      className="block select-none object-contain"
      style={{ width: size, height: size }}
    />
  );
  if (theme === 'dark') return img;
  // Dark background: give the navy mark a white tile so it stays legible.
  return (
    <span
      className="inline-flex shrink-0 items-center justify-center rounded-md bg-white"
      style={{ width: size, height: size, padding: Math.max(2, Math.round(size * 0.08)) }}
    >
      <img
        src={BRAND_MARK_URL}
        alt=""
        aria-hidden="true"
        draggable={false}
        className="block h-full w-full select-none object-contain"
      />
    </span>
  );
}

// ─── Wordmark ─────────────────────────────────────────────────────────────────
// "School" in navy and "One" in sky blue, as on the banner artwork. On a dark
// background "School" turns white; "One" keeps its sky blue in both.

function Wordmark({ size, theme }: { size: number; theme: 'light' | 'dark' }) {
  const school = theme === 'light' ? '#ffffff' : BRAND_NAVY;
  const tagline = theme === 'light' ? 'text-white/70' : 'text-gray-500';
  const fs = Math.round(size * 0.5);
  const fsSub = Math.round(size * 0.28);
  return (
    <div className="flex flex-col justify-center leading-none">
      <span className="font-bold tracking-tight" style={{ fontSize: fs }} aria-label="SchoolOne">
        <span style={{ color: school }}>School</span>
        <span style={{ color: BRAND_SKY }}>One</span>
      </span>
      <span className={`mt-1 font-medium ${tagline}`} style={{ fontSize: fsSub }}>
        {BRAND_TAGLINE}
      </span>
    </div>
  );
}

// ─── Main export ──────────────────────────────────────────────────────────────

export default function Logo({
  size = 40,
  theme = 'light',
  showWordmark = true,
  className = '',
}: LogoProps) {
  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <Mark size={size} theme={theme} />
      {showWordmark && <Wordmark size={size} theme={theme} />}
    </div>
  );
}

/** Icon-only (no wordmark) — convenient shorthand */
export function LogoIcon({
  variant,
  size = 40,
  theme = 'light',
  className = '',
}: Omit<LogoProps, 'showWordmark'>) {
  return <Logo variant={variant} size={size} theme={theme} showWordmark={false} className={className} />;
}
